/**
 * solarFlarePipeline — sun surface with hexagonal convection cells,
 * bright prominences, coronal mass ejections, and surface waves.
 *
 * Composition:
 *   1. Background — deep space gradient with star points + plasma haze.
 *   2. Sun disk — radial gradient hot core to orange limb.
 *   3. Convection cells — hex-granulation pattern across surface.
 *   4. Surface waves — slow ripple distortion across the surface.
 *   5. Prominences — 6 looping arcs extending outward from the limb.
 *   6. CMEs — flowing tendrils ejecting along radial vectors.
 *   7. Solar flares — bright spikes flashing on the limb.
 *
 * Audio reactivity:
 *   bass — brightens the core, expands prominences
 *   mid  — drives convection cell pulse and surface ripples
 *   high — flickers flares, intensifies CME outflow
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SolarFlarePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float h11(float n) {
  return fract(sin(n * 91.345) * 47453.12);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float r = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    r += a * noise2(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return r;
}

// Hex distance function returning nearest hex center and edge dist.
vec3 hexCell2D(vec2 p) {
  const float SQRT3 = 1.7320508;
  vec2 q = vec2(p.x * 1.1547, p.y + p.x * 0.57735);
  vec2 base = floor(q);
  vec2 local = fract(q);
  float parity = mod(base.x + base.y, 3.0);
  float a = step(1.0, parity);
  float b = step(2.0, parity);
  vec2 diagonal = step(local.xy, local.yx);
  vec2 id = base + a - b * diagonal;
  vec2 centerSpace = vec2(
    q.x + floor(0.5 + p.y / 1.5),
    4.0 * p.y / 3.0
  ) * 0.5 + 0.5;
  float centerDist = length(
    (fract(centerSpace) - 0.5) * vec2(1.0, SQRT3 * 0.5)
  );
  return vec3(id, centerDist);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 sunC = vec2(0.0, 0.0);
  float sunR = 0.42 + bass * 0.02;

  // Polar coords relative to sun center.
  vec2 d2 = p - sunC;
  float r = length(d2);
  float theta = atan(d2.y, d2.x);

  // --- 1. Space background ---
  vec3 spaceCol = mix(vec3(0.01, 0.005, 0.04), vec3(0.05, 0.02, 0.10), vUV.y);
  spaceCol += vec3(0.4, 0.3, 0.8) * fbm(p * 1.5 + vec2(uTime * 0.04, 0.0)) * 0.15;
  // Stars.
  vec2 sp = p * 80.0;
  vec2 si = floor(sp);
  vec2 sf = fract(sp);
  float starSeed = hash21(si);
  if (starSeed > 0.985) {
    vec2 c = vec2(0.5);
    float starD = length(sf - c);
    float star = exp(-starD * 200.0);
    star *= 0.5 + 0.5 * sin(uTime * 2.0 + starSeed * 30.0);
    spaceCol += vec3(1.0, 0.95, 0.85) * star * (0.6 + high * 0.6);
  }

  vec3 col = spaceCol;

  // --- 2. Solar disk (only if inside sunR) ---
  if (r < sunR * 1.6) {
    // Normalized position across disk.
    float u = clamp(r / sunR, 0.0, 1.0);
    // Hot core to orange/red limb gradient.
    vec3 deepCore = vec3(1.0, 1.0, 0.95);
    vec3 hotCore = vec3(1.0, 0.85, 0.40);
    vec3 limb = vec3(1.0, 0.45, 0.10);
    vec3 sunCol = mix(deepCore, hotCore, u);
    sunCol = mix(sunCol, limb, smoothstep(0.65, 1.0, u));

    // --- 3. Convection cells (hex granulation) ---
    // Map disk-local position to hex coords.
    float diskX = d2.x / sunR * 3.0;
    float diskY = d2.y / sunR * 3.0;
    // Animate.
    float cellScale = 5.5 + bass * 0.4;
    vec2 hexP = vec2(diskX, diskY) * cellScale;
    hexP += vec2(uTime * 0.04, -uTime * 0.05);
    vec3 h = hexCell2D(hexP);
    float cellId = h.x + h.y * 31.0;
    float edgeDist = h.z;
    float cellSeed = hash21(vec2(cellId, cellId));
    float pulse = 0.5 + 0.5 * sin(uTime * (0.8 + mid * 1.5) + cellSeed * 10.0);
    float cellRing = smoothstep(0.42, 0.32, edgeDist);
    float cellBody = smoothstep(0.5, 0.32, edgeDist);
    // Cells are slightly darker in center, brighter at edge.
    sunCol -= vec3(0.10, 0.08, 0.05) * cellBody * 0.5;
    sunCol += vec3(1.0, 0.7, 0.3) * cellRing * (0.15 + pulse * 0.25 + mid * 0.2);

    // --- 4. Surface waves — radial ripple ---
    float wave = sin(r * 30.0 - uTime * 4.0 + bass * 5.0) * 0.5 + 0.5;
    sunCol += vec3(1.0, 0.7, 0.3) * wave * (0.08 + mid * 0.15) * smoothstep(1.0, 0.6, u);

    // Sunspots — small dark patches on random cells.
    float sunspotMask = step(0.85, cellSeed);
    float sunspot = sunspotMask * smoothstep(0.5, 0.2, edgeDist);
    sunCol -= vec3(0.3, 0.2, 0.05) * sunspot * (0.5 + bass * 0.5);

    // Limb darkening / brightening — corona effect.
    float limbGlow = exp(-(u - 0.92) * 25.0) * step(u, 0.92);
    sunCol += vec3(1.0, 0.7, 0.3) * limbGlow * 0.5;

    col = mix(col, sunCol, smoothstep(sunR * 1.0, sunR * 0.98, r));
  }

  // --- 5. Prominences — looping arcs from limb ---
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float baseAng = fi * 1.0472 + uTime * 0.05;
    float len = 0.18 + 0.10 * sin(uTime * (1.0 + fi * 0.4));
    float intensity = 0.4 + bass * 1.2 + mid * 0.3;
    // Arc points in polar coordinates (relative to sun).
    float minArcD = 1000.0;
    for (int s = 0; s < 16; s++) {
      float fs = float(s) / 15.0;
      // Loop arc: theta starts at baseAng, sweeps out; r goes from sunR outward then back.
      float sweep = sin(fs * 3.14159) * 0.6;
      float ptAng = baseAng + sweep;
      float ptR = sunR + sin(fs * 3.14159) * len;
      // Jitter.
      ptR += (noise2(vec2(fs * 8.0 + fi * 3.0, uTime * 2.0 + fi)) - 0.5) * 0.05;
      vec2 pt = sunC + vec2(cos(ptAng), sin(ptAng)) * ptR;
      float d = length(p - pt);
      if (d < minArcD) minArcD = d;
    }
    float arcGlow = exp(-minArcD * 200.0);
    float arcHalo = exp(-minArcD * 25.0) * 0.15;
    col += vec3(1.0, 0.4, 0.1) * (arcGlow + arcHalo) * intensity;
  }

  // --- 6. CMEs — flowing tendrils along radial vectors ---
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    float cmeAng = fi * 1.2566 + uTime * 0.10;
    float cmeLen = 0.4 + 0.2 * sin(uTime * 0.7 + fi * 1.3);
    float minCD = 1000.0;
    for (int s = 0; s < 14; s++) {
      float fs = float(s) / 13.0;
      // Radial distance flowing outward.
      float cmeR = sunR + fs * cmeLen;
      float cmeAngOff = sin(fs * 3.0 + uTime * 1.5 + fi) * 0.18;
      float ang = cmeAng + cmeAngOff;
      vec2 pt = sunC + vec2(cos(ang), sin(ang)) * cmeR;
      float jitter = (noise2(vec2(fs * 6.0 + fi * 7.0, uTime * 3.0 + fi)) - 0.5) * 0.04;
      pt += vec2(cos(ang + 1.57), sin(ang + 1.57)) * jitter;
      float d = length(p - pt);
      if (d < minCD) minCD = d;
    }
    float cmeGlow = exp(-minCD * 150.0);
    float cmeHalo = exp(-minCD * 20.0) * 0.10;
    float intensity = 0.3 + high * 0.8;
    col += vec3(1.0, 0.55, 0.15) * (cmeGlow + cmeHalo) * intensity;
  }

  // --- 7. Solar flares — bright spikes on limb ---
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float flareAng = fi * 0.7854 + uTime * 0.2;
    float flareLen = 0.05 + 0.06 * sin(uTime * (4.0 + fi * 1.7) + fi * 1.3);
    flareLen *= (0.4 + bass * 1.0);
    float flareOn = step(0.4, sin(uTime * (3.0 + fi) + fi * 1.7) * 0.5 + 0.5);
    vec2 flareTip = sunC + vec2(cos(flareAng), sin(flareAng)) * (sunR + flareLen);
    float fd = length(p - flareTip);
    float flare = exp(-fd * 250.0) * flareOn;
    float flareHalo = exp(-fd * 30.0) * 0.20 * flareOn;
    col += vec3(1.0, 0.85, 0.5) * (flare + flareHalo) * (0.5 + high * 1.5);
  }

  // Corona glow — outer halo.
  float corona = exp(-max(0.0, r - sunR) * 5.0) * 0.6;
  col += vec3(1.0, 0.6, 0.2) * corona * (0.3 + bass * 0.5);

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.3;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeSolarFlareBands(t: number, out: Float32Array): void {
  out[0] = 0.45 + 0.45 * Math.sin(t * 0.9);
  out[1] = 0.55 + 0.40 * Math.abs(Math.sin(t * 1.6));
  out[2] = 0.40 + 0.40 * Math.sin(t * 2.4 + 0.7);
  out[3] = 0.50 + 0.40 * Math.sin(t * 1.4 + 1.0);
  out[4] = 0.50 + 0.40 * Math.sin(t * 2.0 + 2.3);
  out[5] = 0.40 + 0.50 * Math.sin(t * 2.7 + 3.1);
  out[6] = 0.30 + 0.60 * Math.sin(t * 4.5 + 0.5);
  out[7] = 0.40 + 0.50 * Math.sin(t * 6.5 + 1.7);
  out[8] = 0.30 + 0.60 * Math.sin(t * 8.5 + 2.1);
}

export function computeSolarFlareRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.012], [0.012, 0.040], [0.040, 0.080],
    [0.080, 0.180], [0.180, 0.300], [0.300, 0.460],
    [0.460, 0.640], [0.640, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0;
    let count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSolarFlarePipeline(gl: WebGL2RenderingContext): SolarFlarePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    bands: new Float32Array(9),
  };
}

export function drawSolarFlare(
  gl: WebGL2RenderingContext,
  pipe: SolarFlarePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}